import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Textarea from '@cloudscape-design/components/textarea';
import Input from '@cloudscape-design/components/input';
import Alert from '@cloudscape-design/components/alert';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Spinner from '@cloudscape-design/components/spinner';
import { getAIConfig, updateAIConfig, AIConfig, AIConfigExample } from '../../api/tickets';
import { AIConfigSummary } from './ConfigSummaries';

const EMPTY_EXAMPLE: AIConfigExample = { title: '', description: '', comment_summary: '', summary: '' };

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
}

export function AISummarizationSettings() {
  const [current, setCurrent] = useState<AIConfig | null>(null);
  const [lastModified, setLastModified] = useState('');
  const [model, setModel] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [examples, setExamples] = useState<AIConfigExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAIConfig();
      setCurrent(res.config);
      setLastModified(res.lastModified);
      setModel(res.config.model);
      setSystemPrompt(res.config.system_prompt || '');
      setUserPrompt(res.config.user_prompt || res.config.prompt || '');
      setExamples(res.config.examples || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to load AI config: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateAIConfig({
        model,
        system_prompt: systemPrompt || undefined,
        user_prompt: userPrompt || undefined,
        examples: examples.length > 0 ? examples : undefined,
      });
      setMessage({ type: 'success', text: 'AI config saved. New summaries will use this config.' });
      load();
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to save AI config: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  const updateExample = (index: number, field: keyof AIConfigExample, value: string) => {
    setExamples((prev) => prev.map((ex, i) => (i === index ? { ...ex, [field]: value } : ex)));
  };
  const removeExample = (index: number) => {
    setExamples((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Container
      header={
        <Header variant="h2" description="The model and prompts used to generate AI ticket summaries.">
          AI Summarization
        </Header>
      }
    >
      <SpaceBetween size="m">
        {message && (
          <Alert type={message.type} dismissible onDismiss={() => setMessage(null)}>{message.text}</Alert>
        )}

        <Box variant="small" color="text-body-secondary">Last modified: {loading ? 'Loading…' : formatDate(lastModified)}</Box>

        {loading ? <Spinner /> : <AIConfigSummary config={current} />}

        <ExpandableSection headerText="Edit AI configuration">
          <SpaceBetween size="m">
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Model ID</Box>
              <Input value={model} onChange={({ detail }) => setModel(detail.value)} disabled={loading} placeholder="us.amazon.nova-lite-v1:0" />
            </SpaceBetween>

            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">System prompt</Box>
              <Box variant="small" color="text-body-secondary">Defines the AI persona and behavioral rules.</Box>
              <Textarea value={systemPrompt} onChange={({ detail }) => setSystemPrompt(detail.value)} rows={4} disabled={loading} placeholder="You are a senior incident analyst..." />
            </SpaceBetween>

            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">User prompt template</Box>
              <Box variant="small" color="text-body-secondary">Use {'{title}'}, {'{description}'}, {'{comment_summary}'} as placeholders.</Box>
              <Textarea value={userPrompt} onChange={({ detail }) => setUserPrompt(detail.value)} rows={6} disabled={loading} />
            </SpaceBetween>

            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Few-shot examples ({examples.length})</Box>
              <Box variant="small" color="text-body-secondary">Examples teach the model your preferred output format. 1-3 recommended.</Box>
            </SpaceBetween>

            {examples.map((ex, i) => (
              <ExpandableSection
                key={i}
                headerText={`Example ${i + 1}`}
                headerActions={<Button variant="icon" iconName="close" onClick={() => removeExample(i)} />}
              >
                <SpaceBetween size="s">
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Title</Box>
                    <Input value={ex.title} onChange={({ detail }) => updateExample(i, 'title', detail.value)} disabled={loading} />
                  </SpaceBetween>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Description</Box>
                    <Textarea value={ex.description} onChange={({ detail }) => updateExample(i, 'description', detail.value)} rows={3} disabled={loading} />
                  </SpaceBetween>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Comment summary</Box>
                    <Textarea value={ex.comment_summary} onChange={({ detail }) => updateExample(i, 'comment_summary', detail.value)} rows={3} disabled={loading} />
                  </SpaceBetween>
                  <SpaceBetween size="xs">
                    <Box variant="awsui-key-label">Expected summary</Box>
                    <Textarea value={ex.summary} onChange={({ detail }) => updateExample(i, 'summary', detail.value)} rows={8} disabled={loading} />
                  </SpaceBetween>
                </SpaceBetween>
              </ExpandableSection>
            ))}

            <Button iconName="add-plus" onClick={() => setExamples((prev) => [...prev, { ...EMPTY_EXAMPLE }])} disabled={loading}>
              Add example
            </Button>

            <Button variant="primary" onClick={handleSave} loading={saving} disabled={loading}>Save AI config</Button>
          </SpaceBetween>
        </ExpandableSection>
      </SpaceBetween>
    </Container>
  );
}
